import React, { useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  LabelList,
  Label,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  AreaChart,
  Area,
} from "recharts";
import { PieChart as PieIcon, BarChart3, CalendarDays } from "lucide-react";

const COLORS = [
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#8B5CF6",
  "#06B6D4",
  "#F97316",
  "#84CC16",
  "#6366F1",
  "#EAB308",
  "#14B8A6",
  "#EC4899",
];

// 🔹 Función para renderizar la etiqueta externa más grande
const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  value,
}) => {
  const RADIAN = Math.PI / 180;
  const radius = outerRadius * 1.2; // Distancia de la etiqueta
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  // Coordenadas para la línea quebrada
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = cx + (outerRadius + 5) * cos;
  const sy = cy + (outerRadius + 5) * sin;
  const mx = cx + (outerRadius + 25) * cos;
  const my = cy + (outerRadius + 25) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 20; // Brazo horizontal más largo
  const ey = my;
  const textAnchor = cos >= 0 ? "start" : "end";

  // Ocultar etiquetas de segmentos muy pequeños para evitar superposición
  if (percent < 0.03) return null;

  return (
    <g>
      {/* Línea conectora */}
      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
        stroke="#9CA3AF"
        strokeWidth={1.5}
        fill="none"
      />
      <circle cx={ex} cy={ey} r={2} fill="#9CA3AF" stroke="none" />

      {/* Texto: Porcentaje (Negrita y más grande) */}
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 8}
        y={ey}
        textAnchor={textAnchor}
        fill="#111827" // Gris muy oscuro
        fontSize={13}
        fontWeight="700"
        dominantBaseline="central"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>

      {/* Texto: Cantidad (Debajo) */}
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 8}
        y={ey}
        dy={14}
        textAnchor={textAnchor}
        fill="#4B5563"
        fontSize={11}
        fontWeight="500"
        dominantBaseline="central"
      >
        {`(${value} v.)`}
      </text>
    </g>
  );
};

const DashboardExtraCharts = ({
  user,
  data = [],
  handleCrossFilter,
  activeFilters = {},
  dateFilters = {},
  applyDateFilter,
  applyActiveFilters,
}) => {
  //  Aplicando filtros a los datos
  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];

    // Aplicando filtros de fecha
    let filtered = applyDateFilter
      ? applyDateFilter(data, dateFilters.startDate, dateFilters.endDate)
      : data;

    // Aplicando filtros activos (crossfilter)
    filtered = applyActiveFilters ? applyActiveFilters(filtered) : filtered;

    return filtered;
  }, [data, dateFilters, activeFilters, applyDateFilter, applyActiveFilters]);

  const dataConRuta = filteredData.map((viaje) => {
    const deposito = viaje.deposito?.trim().toUpperCase();
    const planta = viaje.planta?.trim().toUpperCase();
    const puerto = viaje.puerto?.trim().toUpperCase();

    return {
      ...viaje,
      ruta:
        deposito && planta && puerto
          ? `${deposito} - ${planta} - ${puerto}`
          : "NO DEFINIDA",
    };
  });

  const groupedByRuta = useMemo(() => {
    if (!dataConRuta || dataConRuta.length === 0) return [];

    const map = {};

    dataConRuta.forEach((viaje) => {
      const ruta = viaje.ruta || "No definida";
      map[ruta] = (map[ruta] || 0) + 1;
    });

    return Object.entries(map)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [dataConRuta]);

  // Lógica del Título y Campo de Agrupación
  const { groupField, chartTitle, subtitle } = useMemo(() => {
    if (user?.tipo === "transportista" || user?.tipo === "cliente") {
      return {
        groupField: "operador",
        chartTitle: "Viajes por Operador - Top 10",
        subtitle: "Operador",
      };
    }

    return {
      groupField: "transportista",
      chartTitle: "Viajes por Transportista - Top 10",
      subtitle: "Transportista",
    };
  }, [user]);

  const viajesPorTracto = useMemo(() => {
    if (!filteredData || !filteredData.length) return [];

    const map = {};
    filteredData.forEach((v) => {
      const tracto = v.tracto || "Sin tracto";
      map[tracto] = (map[tracto] || 0) + 1;
    });

    return Object.entries(map)
      .map(([tracto, value]) => ({ tracto, value }))
      .sort((a, b) => b.value - a.value);
  }, [filteredData]);

  // Agrupar por entidad (Transportista u Operador)
  const groupedData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];
    const map = {};
    filteredData.forEach((viaje) => {
      const key = viaje[groupField] || viaje["transportista"] || "No definido";
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [filteredData, groupField]);

  const operadorPorConductor = useMemo(() => {
    if (!filteredData || !filteredData.length) return [];

    const map = {};
    filteredData.forEach((v) => {
      const key = v.conductor || "Sin conductor";
      map[key] = (map[key] || 0) + 1;
    });

    return Object.entries(map)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 15);
  }, [filteredData]);

  const kmPorTracto = useMemo(() => {
    if (!filteredData || !filteredData.length) {
      console.log("⚠️ DEBUG: No hay filteredData para kmPorTracto");
      return [];
    }

    const map = {};

    // 🔍 DEBUG: Imprimir estructura del primer elemento para ver si existe la key "kilometraje"
    console.log("🔍 DEBUG: Primer elemento raw:", filteredData[0]);
    console.log(
      "🔍 DEBUG: Tipo de kilometraje:",
      typeof filteredData[0]?.kilometraje
    );

    filteredData.forEach((v, index) => {
      const tracto = v.tracto?.trim() || "Sin tracto";

      // Capturamos el valor crudo
      const rawKm = v.kilometraje;

      // Intento de conversión robusto
      let km = 0;

      if (typeof rawKm === "number") {
        km = rawKm;
      } else if (typeof rawKm === "string") {
        // Eliminar comas si vienen como separadores de miles (ej: "1,200")
        // Ojo: Si usan comas decimales (1.500,50), la lógica cambia.
        // Asumo estándar JS (punto decimal).
        const cleanString = rawKm.replace(/,/g, "");
        km = Number(cleanString);
      }

      // 🔍 DEBUG: Ver por qué falla en los primeros 5 items
      if (index < 5) {
        console.log(
          `🔍 DEBUG Item ${index}: Tracto: ${tracto} | Raw: "${rawKm}" | Parsed: ${km}`
        );
      }

      // Si después de limpiar sigue siendo NaN, usar 0
      if (isNaN(km)) km = 0;

      map[tracto] = (map[tracto] || 0) + km;
    });

    const result = Object.entries(map)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    console.log("✅ DEBUG: Resultado final kmPorTracto:", result);

    return result;
  }, [filteredData]);

  // 🔹 Agrupar por tipo de viaje
  const groupedByTipoViaje = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];
    const map = {};
    filteredData.forEach((viaje) => {
      const tipo = viaje.viajetipo || "No definido";
      map[tipo] = (map[tipo] || 0) + 1;
    });
    return Object.entries(map)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [filteredData]);

  const groupedByNaviera = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];

    const map = {};
    filteredData.forEach((viaje) => {
      const naviera = viaje.naviera || "No definida";
      map[naviera] = (map[naviera] || 0) + 1;
    });

    return Object.entries(map)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [filteredData]);

  const groupedByMonth = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];

    const map = {};

    // 🔥 MAPEO CONSISTENTE DE MESES
    const mesesMap = [
      "ene",
      "feb",
      "mar",
      "abr",
      "may",
      "jun",
      "jul",
      "ago",
      "sep",
      "oct",
      "nov",
      "dic",
    ];

    filteredData.forEach((viaje) => {
      if (!viaje.fecha) return;

      // Parsear fecha
      const fechaObj = new Date(viaje.fecha);
      if (!(fechaObj instanceof Date) || isNaN(fechaObj)) return;

      // Usar mapeo directo del índice del mes
      const mesNum = fechaObj.getUTCMonth();
      const mesCorto = mesesMap[mesNum];
      const añoCorto = fechaObj.getUTCFullYear().toString().slice(-2);
      const mes = `${mesCorto}-${añoCorto}`;

      map[mes] = (map[mes] || 0) + 1;
    });

    const mesesOrden = {
      ene: 0,
      feb: 1,
      mar: 2,
      abr: 3,
      may: 4,
      jun: 5,
      jul: 6,
      ago: 7,
      sep: 8,
      oct: 9,
      nov: 10,
      dic: 11,
    };

    return Object.entries(map)
      .map(([month, value]) => {
        const [m, y] = month.split("-");
        const mesNum = mesesOrden[m?.toLowerCase()] ?? 0;
        const añoNum = parseInt(`20${y}`, 10);
        const orden = añoNum * 12 + mesNum;
        return { month, value, orden };
      })
      .sort((a, b) => a.orden - b.orden)
      .map(({ month, value }) => ({ month, value }));
  }, [filteredData]);

  // 🔹 Variables de filtro activo
  const activePieValue = activeFilters[`establecimiento_${groupField}`];
  const activeBarValue = activeFilters["tipo_viaje"];
  const activeMonthValue = activeFilters["mes"];
  const activeRutaValue = activeFilters["ruta"];

  const handleRutaClick = (entry) =>
    handleCrossFilter("ruta", entry?.name, "viaje", "extra");

  // Handlers
  const handlePieClick = (entry) =>
    handleCrossFilter("establecimiento", entry?.name, groupField, "extra");
  const handleBarClick = (entry) =>
    handleCrossFilter("tipo_viaje", entry?.name, "viajetipo", "extra");
  const handleMonthClick = (month) => {
    if (!month) return;

    handleCrossFilter("mes", month.toLowerCase().trim(), "fecha", "extra");
  };

  return (
    <div className="mt-6 space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-col">
          <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
            <PieIcon className="h-4 w-4 text-blue-600 mr-2" />
            {chartTitle}
          </h3>

          <div className="flex flex-col sm:flex-row items-center h-full">
            <div className="w-full sm:w-2/3 h-[300px]">
              {" "}
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 20, right: 40, left: 40, bottom: 20 }}>
                  {" "}
                  <Pie
                    data={groupedData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={0}
                    outerRadius={95}
                    labelLine={false}
                    label={renderCustomizedLabel}
                    onClick={handlePieClick}
                  >
                    {groupedData.map((entry, index) => {
                      const isActive = activePieValue === entry.name;
                      return (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                          stroke={isActive ? "#1D4ED8" : "#fff"}
                          strokeWidth={isActive ? 2 : 1}
                          className="cursor-pointer hover:opacity-90"
                        />
                      );
                    })}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [`${value} viajes`, name]}
                    contentStyle={{
                      borderRadius: "8px",
                      fontSize: "12px",
                      border: "none",
                      boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="w-full sm:w-1/3 mt-2 sm:mt-0 pl-0 sm:pl-2 max-h-[280px] overflow-y-auto custom-scrollbar">
              <ul className="space-y-2">
                {subtitle && (
                  <li className="text-sm font-bold text-gray-800 mb-3 uppercase tracking-wide">
                    {subtitle}
                  </li>
                )}

                {groupedData.map((item, index) => (
                  <li
                    key={index}
                    className="flex items-center text-xs text-gray-600"
                  >
                    <span
                      className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    ></span>
                    <span className="truncate flex-1" title={item.name}>
                      {item.name}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        {user?.tipo === "cliente" ? (
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-col">
            <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
              <BarChart3 className="h-4 w-4 text-blue-600 mr-2" />
              Viajes por Naviera
            </h3>

            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={groupedByNaviera}
                  margin={{ top: 30, right: 30, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#2563EB"
                    strokeWidth={2}
                    dot={{ fill: "#2563EB", r: 4 }}
                    activeDot={{ r: 6 }}
                  >
                    <LabelList
                      dataKey="value"
                      position="top"
                      offset={10}
                      fontSize={12}
                      fill="#374151"
                      fontWeight="600"
                    />
                  </Line>
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-col">
            <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
              <BarChart3 className="h-4 w-4 text-blue-600 mr-2" />
              Viajes por Tipo de Servicio
            </h3>

            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={groupedByTipoViaje}
                  layout="vertical"
                  margin={{ top: 5, right: 40, left: 40, bottom: 5 }}
                  barSize={24}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    horizontal={false}
                    stroke="#E5E7EB"
                  />
                  <XAxis type="number" hide />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 11, fill: "#4B5563" }}
                    width={100}
                    interval={0}
                  />
                  <Tooltip />
                  <Bar
                    dataKey="value"
                    radius={[0, 4, 4, 0]}
                    onClick={handleBarClick}
                  >
                    <LabelList
                      dataKey="value"
                      position="right"
                      fontSize={12}
                      fill="#374151"
                    />
                    {groupedByTipoViaje.map((entry, index) => {
                      const isActive = activeBarValue === entry.name;
                      return (
                        <Cell
                          key={`bar-cell-${index}`}
                          fill={isActive ? "#1D4ED8" : "#3B82F6"}
                          className="cursor-pointer hover:opacity-80"
                        />
                      );
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* 🧾 Sección Inferior */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
        <h2 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
          <CalendarDays className="h-4 w-4 text-blue-600 mr-2" />
          Viajes por Mes
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-6 gap-6">
          <div className="lg:col-span-2 border rounded-lg overflow-hidden flex flex-col max-h-[220px]">
            <div className="bg-gray-50 px-3 py-2 border-b grid grid-cols-2 text-xs font-semibold text-gray-600">
              <span>Mes</span>
              <span className="text-right">Viajes</span>
            </div>
            <div className="overflow-y-auto flex-1 custom-scrollbar">
              <table className="min-w-full text-xs text-gray-700">
                <tbody>
                  {groupedByMonth.map((item, index) => {
                    const isActive = activeMonthValue === item.month;
                    return (
                      <tr
                        key={index}
                        className={`cursor-pointer border-b last:border-0 transition-colors ${
                          isActive
                            ? "bg-blue-50 text-blue-700 font-medium"
                            : "hover:bg-gray-50"
                        }`}
                        onClick={() => handleMonthClick(item.month)}
                      >
                        <td className="px-3 py-2">{item.month}</td>
                        <td className="px-3 py-2 text-right">{item.value}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="lg:col-span-4 h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={groupedByMonth}
                margin={{ top: 35, right: 20, left: 10, bottom: 10 }}
              >
                <defs>
                  <linearGradient id="colorArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#E5E7EB"
                />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 11, fill: "#6B7280" }}
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#6B7280" }}
                  axisLine={false}
                  tickLine={false}
                  width={40}
                  padding={{ top: 20 }}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: "8px",
                    fontSize: "12px",
                    border: "none",
                    boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                  }}
                />

                {/* ÁREA + LÍNEA */}
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#2563EB"
                  strokeWidth={2.4}
                  fill="url(#colorArea)"
                  activeDot={{
                    r: 6,
                    strokeWidth: 2,
                    fill: "#1D4ED8",
                    stroke: "white",
                  }}
                  onClick={(e) =>
                    handleMonthClick(e?.activePayload?.[0]?.payload?.month)
                  }
                  style={{ cursor: "pointer" }}
                >
                  {/* LABELS NUMÉRICOS */}
                  <LabelList
                    dataKey="value"
                    position="top"
                    offset={8}
                    style={{
                      fontSize: "11px",
                      fill: "#374151",
                      fontWeight: 600,
                    }}
                    formatter={(val) => `${val}`}
                  />
                </Area>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      {user?.tipo === "transportista" ? (
        /* GRID 2 x 2 SOLO PARA TRANSPORTISTA */
        <div className="grid grid-cols-1 lg:grid-cols-2 lg:grid-rows-2 gap-6">
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-col">
            <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
              <PieIcon className="h-4 w-4 text-blue-600 mr-2" />
              Viajes por Ruta - Top 10
            </h3>
            <div className="flex flex-col sm:flex-row items-center h-full">
              <div className="w-full sm:w-2/3 h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart
                    margin={{ top: 20, right: 40, left: 40, bottom: 20 }}
                  >
                    <Pie
                      data={groupedByRuta}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={0}
                      outerRadius={95}
                      labelLine={false}
                      label={renderCustomizedLabel}
                      onClick={handleRutaClick}
                    >
                      {groupedByRuta.map((entry, index) => {
                        const isActive = activeRutaValue === entry.name;
                        return (
                          <Cell
                            key={`cell-ruta-${index}`}
                            fill={COLORS[index % COLORS.length]}
                            stroke={isActive ? "#1D4ED8" : "#fff"}
                            strokeWidth={isActive ? 2 : 1}
                            className="cursor-pointer hover:opacity-90"
                          />
                        );
                      })}
                    </Pie>

                    <Tooltip
                      formatter={(value, name) => [`${value} viajes`, name]}
                      contentStyle={{
                        borderRadius: "8px",
                        fontSize: "12px",
                        border: "none",
                        boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="w-full sm:w-1/3 mt-2 sm:mt-0 pl-0 sm:pl-2 max-h-[280px] overflow-y-auto custom-scrollbar">
                <ul className="space-y-2">
                  {groupedByRuta.map((item, index) => (
                    <li
                      key={index}
                      className="flex items-center text-xs text-gray-600"
                    >
                      <span
                        className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                        style={{
                          backgroundColor: COLORS[index % COLORS.length],
                        }}
                      />
                      <span className="truncate flex-1" title={item.name}>
                        {item.name}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          <div className="bg-white p-5 rounded-xl shadow-md border border-gray-100">
            <h3 className="text-base font-semibold text-gray-800 mb-4">
              Viajes por tracto
            </h3>

            <div className="h-[260px] w-full">
              <ResponsiveContainer>
                <LineChart
                  data={viajesPorTracto}
                  margin={{ top: 35, right: 25, left: 10, bottom: 40 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis
                    dataKey="tracto"
                    tick={{ fontSize: 10 }}
                    tickLine={false}
                    axisLine={{ stroke: "#D1D5DB" }}
                  >
                    <Label
                      value="Tracto"
                      position="bottom"
                      offset={20}
                      style={{ fill: "#6B7280", fontSize: 12, fontWeight: 600 }}
                    />
                  </XAxis>
                  <YAxis
                    tick={{ fontSize: 10 }}
                    axisLine={{ stroke: "#D1D5DB" }}
                    tickLine={false}
                  >
                    <Label
                      value="Cantidad de viajes"
                      angle={-90}
                      position="left"
                      offset={10}
                      style={{ fill: "#6B7280", fontSize: 12, fontWeight: 600 }}
                    />
                  </YAxis>

                  <Tooltip
                    contentStyle={{
                      borderRadius: "8px",
                      borderColor: "#E5E7EB",
                      padding: "6px 10px",
                      fontSize: "12px",
                    }}
                    labelStyle={{ fontSize: "12px", fontWeight: 600 }}
                  />

                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#2563EB"
                    strokeWidth={2.5}
                    dot={{
                      r: 4,
                      strokeWidth: 1.5,
                      fill: "#2563EB",
                      stroke: "white",
                    }}
                    activeDot={{ r: 6 }}
                  >
                    <LabelList
                      dataKey="value"
                      position="top"
                      offset={12}
                      fontSize={12}
                      fill="#374151"
                      style={{ fontWeight: 600 }}
                    />
                  </Line>
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Recuento por conductor
            </h3>

            <div className="h-[260px] w-full">
              <ResponsiveContainer>
                <BarChart
                  data={operadorPorConductor}
                  margin={{ top: 20, right: 20, left: 10, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />

                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 10 }}
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                    height={60}
                  />

                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />

                  <Bar
                    dataKey="value"
                    fill="#3B82F6"
                    radius={[6, 6, 0, 0]}
                    barSize={30}
                  >
                    <LabelList
                      dataKey="value"
                      position="top"
                      fontSize={12}
                      fill="#1F2937"
                      offset={5}
                      style={{ fontWeight: "600" }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Kilómetros por tracto
            </h3>

            <div className="h-[260px] w-full">
              <ResponsiveContainer>
                <BarChart
                  data={kmPorTracto}
                  layout="vertical"
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />

                  <XAxis
                    type="number"
                    domain={[0, "dataMax"]}
                    tick={{ fontSize: 10 }}
                  />

                  <YAxis
                    type="category"
                    dataKey="name"
                    width={100}
                    tick={{ fontSize: 10 }}
                  />

                  <Tooltip formatter={(v) => `${v} km`} />

                  <Bar
                    dataKey="value"
                    fill="#10B981"
                    barSize={18}
                    radius={[0, 6, 6, 0]}
                  >
                    <LabelList
                      dataKey="value"
                      position="inside"
                      fontSize={11}
                      fill="#FFFFFF"
                      formatter={(val) => `${val} km`}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      ) : (
        /* SI NO ES TRANSPORTISTA → SOLO GRÁFICO DE RUTA SIN GRID */
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-col">
          <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
            <PieIcon className="h-4 w-4 text-blue-600 mr-2" />
            Viajes por Ruta
          </h3>

          <div className="flex flex-col sm:flex-row items-center h-full">
            <div className="w-full sm:w-2/3 h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 20, right: 40, left: 40, bottom: 20 }}>
                  <Pie
                    data={groupedByRuta}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={0}
                    outerRadius={95}
                    labelLine={false}
                    label={renderCustomizedLabel}
                  >
                    {groupedByRuta.map((entry, index) => (
                      <Cell
                        key={`cell-ruta-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="w-full sm:w-1/3 mt-2 sm:mt-0 pl-0 sm:pl-2 max-h-[280px] overflow-y-auto">
              <ul className="space-y-2">
                {groupedByRuta.map((item, index) => (
                  <li
                    key={index}
                    className="flex items-center text-xs text-gray-600"
                  >
                    <span
                      className="w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span className="truncate">{item.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardExtraCharts;
